<script lang="ts" setup>
import arrowDown from "@/assets/image/icons/arrow-down.svg";
import type { RouteName } from "@/router";

const props = withDefaults(
  defineProps<{
    carousel: {
      active?: boolean;
      image: string;
      alt?: string;
      text?: string;
      icon?: string;
      routeName?: RouteName;
      id?: number;
      smImage?: string;
    }[];
  }>(),
  {}
);

const activeIndex = ref(props.carousel.findIndex((c) => c.active));
const viewportRef = ref<HTMLElement | null>(null); // parent (viewport)
const trackRef = ref<HTMLElement | null>(null); // inner flex container we translate
const translateX = ref(0);

// calculate translateX based on active card
const computeTranslate = () => {
  const viewport = viewportRef.value;
  const track = trackRef.value;
  if (!viewport || !track) return 0;

  const viewportW = viewport.clientWidth;
  const trackW = track.scrollWidth;

  // boundaries: 0 (align left), minNeg (align right)
  const minNeg = Math.min(0, viewportW - trackW);

  // active card element
  const cardEls = track.querySelectorAll(".hero-card") as unknown as HTMLElement[];
  const el = cardEls[activeIndex.value];
  if (!el) return translateX.value;

  const cardLeft = el.offsetLeft;
  const cardW = el.offsetWidth;
  const cardCenter = cardLeft + cardW / 2;
  const centerDesired = viewportW / 2 - cardCenter;

  // RULES:
  // 1) If index = 0 → align left
  if (activeIndex.value === 0) {
    return 0;
  }

  // 2) If last index → align right
  if (activeIndex.value === props.carousel.length - 1) {
    return minNeg;
  }

  // 3) Otherwise center the active card (clamped to bounds)
  return Math.max(minNeg, Math.min(0, centerDesired));
};

const applyTranslate = () => {
  nextTick(() => {
    translateX.value = computeTranslate();
  });
};

onMounted(() => {
  applyTranslate();
  window.addEventListener("resize", applyTranslate);
});
onBeforeUnmount(() => {
  window.removeEventListener("resize", applyTranslate);
});

// navigation
const setActive = (i: number) => {
  activeIndex.value = i;
  // for (const [idx, c] of props.carousel.entries()) c.active = idx === i;
  applyTranslate();
};
const prev = () => {
  if (activeIndex.value > 0) setActive(activeIndex.value - 1);
};
const next = () => {
  if (activeIndex.value < props.carousel.length - 1) setActive(activeIndex.value + 1);
};

// if active flags change externally
watch(props.carousel, applyTranslate, { deep: true });
</script>

<template>
  <!-- VIEWPORT (parent, overflow-hidden) -->
  <div ref="viewportRef" class="relative h-full max-h-[892px] min-h-[324px] w-full max-w-[1275px] overflow-hidden">
    <!-- TRACK (translated container) -->
    <div
      ref="trackRef"
      class="flex h-full space-x-4 transition-transform duration-300 ease-in-out will-change-transform [@media(min-width:1307px)]:space-x-1"
      :style="{ transform: `translateX(${translateX}px)` }"
    >
      <div v-for="(item, i) in carousel" :key="i" class="hero-card flex-shrink-0">
        <!-- <slot name="default" :item="item" :index="i" /> -->
        <slot name="default" :item="item" :active="activeIndex === i" />
      </div>
    </div>

    <!-- Left button: hidden if activeIndex = 0 -->
    <button
      v-show="activeIndex > 0"
      class="bg-text-dark absolute top-0 left-0 z-10 flex h-full w-[32px] cursor-pointer items-center justify-center opacity-80 transition-opacity duration-300 hover:opacity-100"
      aria-label="previous"
      @click="prev"
    >
      <img :src="arrowDown" alt="arrow left" class="rotate-90" />
    </button>

    <!-- Right button: hidden if last index -->
    <button
      v-show="activeIndex < carousel.length - 1"
      class="bg-text-dark absolute top-0 right-0 z-10 flex h-full w-[32px] cursor-pointer items-center justify-center opacity-80 transition-opacity duration-300 hover:opacity-100"
      aria-label="next"
      @click="next"
    >
      <img :src="arrowDown" alt="arrow right" class="rotate-270" />
    </button>
  </div>
</template>
